import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Modal, Field, Badge, Empty, SkeletonTable, Pagination, fmtDate, daysUntil, label } from '../components/ui.jsx';
import { useConfirm, useToast } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import { IconPlus } from '../components/icons.jsx';

const SCOPES = ['territory', 'client'];
const TERRITORIES = ['Italy', 'Morocco', 'Other'];
const STATUSES = ['active', 'expired', 'cancelled'];
const TONE = { active: 'green', expired: 'red', cancelled: 'gray' };
const blank = {
  title: '', scope_type: 'territory', territory: 'Morocco', client_id: '', manufacturer_id: '',
  commission_pct: '', start_date: '', end_date: '', status: 'active', notes: '',
};

export default function Exclusivities() {
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const [list, setList] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('page', String(page));
    params.set('per_page', '20');
    const res = await api.get(`/exclusivities?${params}`);
    setList(res.data);
    setMeta(res.meta || null);
  }, [status, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status]);
  useEffect(() => { api.get('/companies').then((r) => setCompanies(r.data)).catch(() => setCompanies([])); }, []);

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Exclusivities')}</h1>
        {canEdit && <button className="btn primary" onClick={() => setEditing({ ...blank })}><IconPlus width={15} height={15} /> {t('New exclusivity')}</button>}
      </div>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('All statuses')}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>

      {!list ? <SkeletonTable cols={canEdit ? 7 : 6} /> : list.length === 0 ? <Empty>{t('No exclusivity agreements yet.')}</Empty> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>{t('Title')}</th><th>{t('Scope')}</th><th>%</th><th>{t('Projects')}</th><th>{t('Status')}</th><th>{t('Ends')}</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {list.map((e) => {
                const d = daysUntil(e.end_date);
                const scope = e.scope_type === 'client'
                  ? `${t('Client')}: ${e.client_name || '—'}`
                  : `${t('Territory')}: ${e.territory || '—'}`;
                return (
                  <tr key={e.id}>
                    <td className="strong">
                      {e.title}
                      {e.manufacturer_name && <div className="muted sm">{t('Brand')}: {e.manufacturer_name}</div>}
                    </td>
                    <td>{scope}</td>
                    <td>{e.commission_pct != null ? `${e.commission_pct}%` : '—'}</td>
                    <td>{e.project_count}</td>
                    <td><Badge tone={TONE[e.status]}>{label(e.status)}</Badge></td>
                    <td className={d !== null && d < 60 && e.status === 'active' ? 'soon' : ''}>{fmtDate(e.end_date)}</td>
                    {canEdit && <td className="row-actions"><button className="btn ghost sm" onClick={() => setEditing(e)}>{t('Open')}</button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pagination meta={meta} onPage={setPage} />

      {editing && (
        <ExclusivityForm initial={editing} companies={companies} isAdmin={user.role === 'admin'}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ExclusivityForm({ initial, companies, isAdmin, onClose, onSaved }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const clients = companies.filter((c) => c.type === 'client');
  const manufacturers = companies.filter((c) => c.type === 'manufacturer');

  async function save() {
    setBusy(true); setErrors({});
    try {
      if (isNew) await api.post('/exclusivities', form);
      else await api.put(`/exclusivities/${initial.id}`, form);
      toast(isNew ? t('Exclusivity created') : t('Exclusivity saved'));
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!(await confirm({ title: t('Delete exclusivity'), message: t('Delete this exclusivity? Linked projects keep their data but lose the link.'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/exclusivities/${initial.id}`);
    toast(t('Exclusivity deleted'));
    onSaved();
  }

  return (
    <Modal title={isNew ? t('New exclusivity') : form.title} onClose={onClose}
      dirty={JSON.stringify(form) !== JSON.stringify(initial)}
      footer={
        <div className="modal-actions">
          {!isNew && isAdmin && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      <Field label={t('Title')} error={errors.title}><input value={form.title} onChange={set('title')} /></Field>
      <div className="grid-2">
        <Field label={t('Scope')}><select value={form.scope_type} onChange={set('scope_type')}>{SCOPES.map((s) => <option key={s} value={s}>{t(s)}</option>)}</select></Field>
        {form.scope_type === 'territory' ? (
          <Field label={t('Territory')}><select value={form.territory || ''} onChange={set('territory')}><option value="">—</option>{TERRITORIES.map((ter) => <option key={ter} value={ter}>{t(ter)}</option>)}</select></Field>
        ) : (
          <Field label={t('Client')}>
            <select value={form.client_id || ''} onChange={set('client_id')}>
              <option value="">—</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        )}
      </div>
      <div className="grid-2">
        <Field label={t('Brand / manufacturer (optional)')}>
          <select value={form.manufacturer_id || ''} onChange={set('manufacturer_id')}>
            <option value="">—</option>
            {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <Field label={t('Default commission %')}><input type="number" step="0.01" value={form.commission_pct ?? ''} onChange={set('commission_pct')} /></Field>
      </div>
      <div className="grid-3">
        <Field label={t('Start')} error={errors.start_date}><input type="date" value={form.start_date || ''} onChange={set('start_date')} /></Field>
        <Field label={t('End / expiry')} error={errors.end_date}><input type="date" value={form.end_date || ''} onChange={set('end_date')} /></Field>
        <Field label={t('Status')}><select value={form.status} onChange={set('status')}>{STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
      </div>
      <Field label={t('Notes')}><textarea rows={2} value={form.notes || ''} onChange={set('notes')} /></Field>
    </Modal>
  );
}
